import { useMemo } from "react";
import { getVisualizationColors } from "../../../theme/colors";
import { BarChartColor, BarChartProcessedData, BarChartSeries } from "./types";

/** Extract a string color for legend display from a BarChartColor */
function getLegendColor(color: BarChartColor | BarChartColor[]): string {
  if (Array.isArray(color)) {
    const first = color[0];
    return typeof first === "string" ? first : "#888888";
  }
  return typeof color === "string" ? color : "#888888";
}

export const useBarChartData = (
  series: BarChartSeries[],
  labels: string[],
  stacked: boolean,
  horizontal = false,
  maxBarThickness = 32,
): BarChartProcessedData => {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return useMemo(() => {
    const colors = getVisualizationColors(series.length);

    const coloredSeries = series.map((s, i) => {
      // If the series has a color array (one color per bar), use it as-is
      // Otherwise use the single color or default color
      const color = Array.isArray(s.color) ? s.color : s.color || colors[i];

      return { ...s, color };
    });

    // For stacked charts, reverse the order so larger series (listed first) render on top
    // This ensures rounded corners appear on the larger segments
    const orderedSeries = stacked
      ? [...coloredSeries].reverse()
      : coloredSeries;

    const normalizedSeries = coloredSeries.map((s, i) => ({
      name: s.name,
      data: s.data.map((value) => ({ value })),
      // Use legendColor if provided, otherwise extract from color
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      color: s.legendColor || getLegendColor(s.color || colors[i]),
    }));

    // For vertical stacked charts, the plugin handles rounding so set borderRadius to 0
    // For horizontal charts and non-stacked charts, use native borderRadius
    const usePluginForRounding = stacked && !horizontal;

    const datasets = orderedSeries.map((s) => ({
      label: s.name,
      // For stacked charts, replace 0 values with null so they don't render
      data: stacked ? s.data.map((v) => (v === 0 ? null : v)) : s.data,
      backgroundColor: s.color, // Can be string, array, or pattern
      borderRadius: usePluginForRounding ? 0 : 4,
      stack: stacked ? "stack" : undefined,
      maxBarThickness,
    }));

    return { labels, datasets, normalizedSeries };
  }, [series, labels, stacked, horizontal, maxBarThickness]);
};
